import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import {
    isPrerenderRun,
    setPrerenderRouteKey,
    markNavReady,
    isContentReady,
    markDispatched,
} from './prerenderCoordinator';

function upsertApolloStateScript(state: any) {
    const json = JSON.stringify(state).replace(/</g, '\\u003c');
    const SCRIPT_ID = '__APOLLO_STATE__';

    let el = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!el) {
        el = document.createElement('script');
        el.id = SCRIPT_ID;
        document.head.prepend(el);
    }
    el.text = `window.__APOLLO_STATE__ = ${json};`;
}

export default function PrerenderGate({ navReady }: { navReady: boolean }) {
    const { pathname } = useLocation();
    const client = useApolloClient();

    useLayoutEffect(() => {
        if (!isPrerenderRun()) return;
        setPrerenderRouteKey(pathname);
    }, [pathname]);

    useEffect(() => {
        if (!isPrerenderRun()) return;
        if (navReady) markNavReady();
    }, [navReady]);

    const pollRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isPrerenderRun()) return;

        if (window.__PRERENDER_DISPATCHED_FOR__ === pathname) return;

        if (!navReady) return;

        const tryDispatch = () => {
            if (window.__PRERENDER_DISPATCHED_FOR__ === pathname) return true;

            if (!isContentReady()) return false;

            try {
                upsertApolloStateScript(client.extract());
            } catch (e) {
                console.warn('[prerender] apollo extract failed', e);
            }

            markDispatched(pathname);
            document.dispatchEvent(new Event('prerender-ready'));
            return true;
        };

        if (tryDispatch()) return;

        pollRef.current = window.setInterval(() => {
            if (tryDispatch() && pollRef.current) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }, 50);

        return () => {
            if (pollRef.current) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [pathname, client, navReady]);

    return null;
}
